import moment from "moment";

export type GstStatusKey = "filed" | "miss" | "blank" | "notDue" | "off";

export interface GstStatusStyle {
  mark: string;
  label: string;
  bg: string;
  ink: string;
  tooltip: string;
}

// Colors ported directly from the reference mockup's CSS custom properties
// (--filed-bg/--filed-ink etc.) so the new grid matches it exactly. Filed is
// always green - a Filed record is never split into "late" vs "on time".
// "blank" is a genuine gap between two Filed periods (backend sends an empty
// status for it) - rendered as an empty box, not a colored mark. "notDue" is
// the current or a still-upcoming period with no Filed record yet.
export const GST_STATUS_STYLE: Record<GstStatusKey, GstStatusStyle> = {
  filed:  { mark: "F", label: "Filed",          bg: "#dcfce7",      ink: "#15803d", tooltip: "Return Filed on the GST Portal" },
  miss:   { mark: "N", label: "Not Filed",      bg: "#fee2e2",      ink: "#b91c1c", tooltip: "Return Filing is Due but not Filed on the GST Portal" },
  blank:  { mark: "",  label: "Not Required",   bg: "#f1f5f9",      ink: "#94a3b8", tooltip: "Period for which GST Return Filing is not required due to Taxpayer opting for the QRMP Scheme | Due Date Elapsed in Case of GSTR-1A" },
  notDue: { mark: "-", label: "Not Due Yet",    bg: "#f1f5f9",      ink: "#64748b", tooltip: "Upcoming Return Periods for which GST filing is not due" },
  off:    { mark: "-", label: "Not Applicable", bg: "#ffedd5",      ink: "#c2410c", tooltip: "Period starting from the beginning of the FY up to the Preceeding month of GST Registartion" },
};

// Standard GST compliance-calendar due day-of-month, per return type. These
// are the public due dates, not derived from any data we hold - flagged in
// the redesign plan for the user to double-check against their own calendar.
export const RETURN_DUE_DAY: Record<string, number> = {
  GSTR1: 11,
  GSTR1A: 11,
  GSTR2X: 20,
  GSTR3B: 20,
  CMP08: 18,
  GSTR6: 13,
  GSTR7: 10,
  GSTR8: 10,
};

// Annual returns have no monthly/quarterly period to add a day-of-month to -
// they're due on a fixed date relative to the FY's end. GSTR4X's periodValue
// also comes through as "" (no sub-period), so periodEndDate() can't be used
// for any of these.
const ANNUAL_DUE_DATE: Record<string, { month: number; day: number }> = {
  GSTR4X: { month: 6, day: 30 },  // 30 Jun following the FY
  GSTR9: { month: 12, day: 31 },  // 31 Dec following the FY
  GSTR9C: { month: 12, day: 31 },
};

// The calendar end-date of a monthly/quarterly period, given the selected FY
// string ("2025-26") and the period's value ("04".."03" for months, or
// "06"|"09"|"12"|"03" for quarters).
function periodEndDate(selectedFY: string, periodValue: string, granularity: "month" | "quarter"): moment.Moment {
  const startYear = parseInt(selectedFY.split("-")[0], 10);
  const month = parseInt(periodValue, 10); // 1-12
  const calendarYear = month >= 4 ? startYear : startYear + 1;
  return moment(`${calendarYear}-${periodValue}-01`, "YYYY-MM-DD").endOf(granularity === "quarter" ? "month" : "month");
}

export function getDueDate(rtntype: string, selectedFY: string, periodValue: string, granularity: "month" | "quarter"): moment.Moment {
  const annual = ANNUAL_DUE_DATE[rtntype];
  if (annual) {
    const startYear = parseInt(selectedFY.split("-")[0], 10);
    return moment(`${startYear + 1}-${String(annual.month).padStart(2, "0")}-${annual.day}`, "YYYY-MM-DD");
  }
  const end = periodEndDate(selectedFY, periodValue, granularity);
  const day = RETURN_DUE_DAY[rtntype] ?? 20;
  return end.clone().add(1, "month").date(day);
}

interface RowLike {
  returnsData?: Array<{ status?: string | null; dof?: string | null; arn?: string | null }>;
}

// The single source of truth for what a period cell shows. The backend now
// fully decides Filed/NA/Not Filed - findAll() only leaves a period without
// an explicit status when it's a genuine gap between two Filed periods, which
// renders as an empty box rather than a computed due-date guess.
export function resolveGstStatus(
  row: RowLike | undefined,
  dueDate: moment.Moment
): GstStatusKey {
  const status = row?.returnsData?.[0]?.status;
  if (status === "NA") return "off";
  if (status === "Filed") return "filed";
  if (status === "Not Filed") return "miss";
  if (status === "Not due yet") return "notDue";
  return "blank";
}
